import { FC, ReactNode } from "react";
import { Box, Button, Typography } from "@mui/material";
import { makeSxStyles } from "ui";

export interface EmptyStateProps {
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  actionDisabled?: boolean;
  actionStartIcon?: ReactNode;
  icon?: ReactNode;
}

const EmptyState: FC<EmptyStateProps> = ({
  title,
  description,
  actionLabel,
  onAction,
  actionDisabled,
  actionStartIcon,
  icon,
}) => {
  const styles = useStyles();

  return (
    <Box sx={styles.container}>
      {icon}
      <Typography variant="h6" fontWeight="bold" color="gray1">
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="gray1" sx={styles.description}>
          {description}
        </Typography>
      )}
      {actionLabel && onAction && (
        <Button
          variant="outlined"
          startIcon={actionStartIcon}
          onClick={onAction}
          disabled={actionDisabled}
          sx={styles.action}
        >
          {actionLabel}
        </Button>
      )}
    </Box>
  );
};

/**
 * Props for the common "search/filter returned nothing" flavor of EmptyState:
 * "No <entityLabel> found" / "We couldn't find any <entityLabel> matching
 * your search." / "Clear search". Keeps that copy consistent across list
 * pages instead of re-typing it per feature.
 */
export function noResultsEmptyState(
  entityLabel: string,
  onClear: () => void,
): EmptyStateProps {
  return {
    title: `No ${entityLabel} found`,
    description: `We couldn't find any ${entityLabel} matching your search.`,
    actionLabel: "Clear search",
    onAction: onClear,
  };
}

const useStyles = makeSxStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    textAlign: "center",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: "8px",
    padding: "48px 24px",
  },
  description: {
    maxWidth: "420px",
  },
  action: {
    mt: "4px",
  },
}));

export default EmptyState;
